import React, { useEffect, useState } from 'react';
import { roomPayApi } from '../services/roomPayApi';

interface RoomPayQRModalProps {
  isOpen: boolean;
  onClose: () => void;
  propertyId: number;
  propertyTitle: string;
  roomNumber?: string;
  payRoomCode?: string | null;
  payUrl?: string | null;
  onUpdated?: (data: { pay_room_code?: string; pay_url?: string | null }) => void;
}

const RoomPayQRModal: React.FC<RoomPayQRModalProps> = ({
  isOpen,
  onClose,
  propertyId,
  propertyTitle,
  roomNumber,
  payRoomCode: initialCode,
  payUrl: initialUrl,
  onUpdated,
}) => {
  const [payRoomCode, setPayRoomCode] = useState(initialCode || '');
  const [payUrl, setPayUrl] = useState(initialUrl || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    setPayRoomCode(initialCode || '');
    setPayUrl(initialUrl || '');
    setError(null);

    const load = async () => {
      if (initialCode && initialUrl) return;

      setLoading(true);
      try {
        let info = await roomPayApi.getManageInfo(propertyId);
        if (!info.has_pay_qr && info.room_number) {
          info = await roomPayApi.autoCode(propertyId);
        }
        if (info.pay_room_code) {
          setPayRoomCode(info.pay_room_code);
          setPayUrl(info.pay_url || '');
          onUpdated?.({ pay_room_code: info.pay_room_code, pay_url: info.pay_url });
        } else if (!info.room_number) {
          setError('Phòng chưa có mã số phòng. Hãy nhập mã phòng khi đăng/sửa sản phẩm.');
        } else {
          setError('Chưa tạo được mã QR. Vui lòng thử lại sau.');
        }
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Không thể tải QR cố định');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [isOpen, propertyId, initialCode, initialUrl, onUpdated]);

  if (!isOpen) return null;

  const qrImageUrl = payRoomCode ? roomPayApi.getQrImageUrl(payRoomCode) : '';

  const handleDownload = () => {
    if (!payRoomCode) return;
    const link = document.createElement('a');
    link.href = qrImageUrl;
    link.download = `qr-${payRoomCode}.png`;
    link.click();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-black">QR phòng</h3>
            <p className="mt-1 text-sm text-black/60 line-clamp-2">{propertyTitle}</p>
            {roomNumber && (
              <p className="mt-0.5 text-xs text-black/50">Mã phòng: {roomNumber}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-black/50 hover:bg-black/5 hover:text-black"
            aria-label="Đóng"
          >
            <i className="fas fa-times" />
          </button>
        </div>

        {loading && (
          <div className="py-10 text-center text-sm text-black/60">
            <i className="fas fa-spinner fa-spin mr-2" />
            Đang tải QR...
          </div>
        )}

        {!loading && error && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {error}
          </div>
        )}

        {!loading && !error && payRoomCode && (
          <div className="space-y-4">
            <div className="flex justify-center rounded-xl border border-gray-200 bg-white p-4">
              <img
                src={qrImageUrl}
                alt={`QR ${payRoomCode}`}
                className="h-48 w-48 object-contain"
              />
            </div>

            <p className="text-xs text-black/50">
              QR này chỉ dùng để nhận diện phòng <strong>{payRoomCode}</strong>. In/dán tại cửa phòng.
            </p>

            <button
              type="button"
              onClick={handleDownload}
              className="w-full rounded-full border border-[#C4A35A] py-2.5 text-sm font-semibold text-[#C4A35A] hover:bg-[#C4A35A]/5"
            >
              <i className="fas fa-download mr-2" />
              Tải ảnh QR
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoomPayQRModal;
